import { useOrganizationRosterListQuery } from '@client/graphql/__generated__/main-operations';
import {
  OrganizationRosterItemFragment,
  PartnerType,
} from '@client/graphql/__generated__/types';
import { useMemo } from 'react';
import { useAppContext } from '~/services/main/contexts/AppContext';
import { RosterEntry, RosterPreference } from '../types';

type FragmentPreference = OrganizationRosterItemFragment['preferences'][number];

function transformPreference(
  pref: FragmentPreference | undefined
): RosterPreference | null {
  if (!pref) return null;

  return {
    id: pref.id,
    partnerType: pref.partnerType,
    preferenceType: pref.preferenceType,
    partner: pref.partner ?? null,
    partnerContact: pref.partnerContact
      ? {
          id: pref.partnerContact.id,
          firstName: pref.partnerContact.firstName,
          lastName: pref.partnerContact.lastName,
          partner: pref.partnerContact.partner ?? null,
        }
      : null,
  };
}

function transformRosterEntry(
  entry: OrganizationRosterItemFragment
): RosterEntry {
  const getPreference = (type: PartnerType) =>
    entry.preferences.find((p) => p.partnerType === type);

  return {
    id: entry.id,
    isContactable: entry.isContactable,
    agent: {
      id: entry.contact.id,
      firstName: entry.contact.firstName,
      lastName: entry.contact.lastName,
      email: entry.contact.email,
    },
    preferences: {
      [PartnerType.LENDER]: transformPreference(
        getPreference(PartnerType.LENDER)
      ),
      [PartnerType.TITLE]: transformPreference(
        getPreference(PartnerType.TITLE)
      ),
      [PartnerType.INSURANCE]: transformPreference(
        getPreference(PartnerType.INSURANCE)
      ),
    },
  };
}

export function useOrganizationRoster({
  searchQuery,
}: {
  searchQuery?: string;
}) {
  const { selectedOrganization } = useAppContext();

  const { data, loading, error, refetch } = useOrganizationRosterListQuery({
    variables: {
      organizationId: selectedOrganization?.id ?? '',
      filters: {
        searchQuery: searchQuery ?? undefined,
      },
      perPage: 100,
    },
    skip: !selectedOrganization?.id,
  });

  const rosterEntries = useMemo(() => {
    return (data?.organizationRosterList?.results ?? []).map(
      transformRosterEntry
    );
  }, [data]);

  const pageInfo = data?.organizationRosterList?.pageInfo;

  return {
    rosterEntries,
    loading,
    error,
    refetch,
    pageInfo,
  };
}
